import React from 'react';
import {
    BREAKPOINTS, BREAKPOINT_LABELS, BREAKPOINT_ICONS, COLOR_TOKENS, GRADIENT_TOKENS, FONT_TOKENS, FONT_SIZE_TOKENS,
    FONT_WEIGHT_TOKENS, SPACING_TOKENS, RADIUS_TOKENS, SHADOW_TOKENS, ALIGN_TOKENS,
} from '../tokens';
import CommitField from '../CommitField';
import FocalPad from './FocalPad';
import { isImageType, FIT_OPTIONS, parseObjectPosition, objectPositionValue } from '../imageFit';
import { parseRotation, rotationValue, clampPercent } from '../freeLayout';

/**
 * Per-component style editor — every control picks from tokens.js's fixed
 * token lists, never free text, so a builder-authored page can never carry
 * an arbitrary/unsafe CSS value (the backend's UNSAFE_STYLE_PATTERN scan
 * in utils/landingPageSchema.js is still the real security boundary this
 * is defense-in-depth alongside, not a replacement for).
 *
 * Writes into node.style[breakpoint][cssProperty] — see
 * frontend/src/pages/Portal/landingPageStyles.js for how the guest-facing
 * renderer turns these three breakpoint buckets into real @media-query
 * CSS, and LandingBuilder.jsx's CanvasNode for how the builder's own
 * (simulated-width) canvas previews the currently-selected breakpoint.
 */
export default function StylePanel({ node, breakpoint, onBreakpointChange, onStyleChange, onStylePatch, onAlign, showDeviceTabs = true }) {
    if (!node) {
        return <div className="lb-style-panel"><p className="lb-inspector-empty">Select a component to edit its style.</p></div>;
    }

    const current = node.style?.[breakpoint] || {};
    const set = (prop, value) => onStyleChange(node.id, breakpoint, prop, value);

    return (
        <div className="lb-style-panel">
            {!showDeviceTabs && (
                <p className="lb-auto-note">
                    <i className="fas fa-magic"></i> Design for <strong>Desktop</strong> — every other device adapts automatically.
                    Use “Customise per device” in the toolbar only to override one.
                </p>
            )}
            {showDeviceTabs && (
            <div className="lb-breakpoint-tabs">
                {BREAKPOINTS.map((bp) => (
                    <button
                        key={bp}
                        type="button"
                        className={`lb-breakpoint-tab ${breakpoint === bp ? 'active' : ''}`}
                        onClick={() => onBreakpointChange(bp)}
                        title={`Edit styles for ${BREAKPOINT_LABELS[bp]}`}
                        aria-label={`Edit styles for ${BREAKPOINT_LABELS[bp]}`}
                    >
                        <i className={`fas ${BREAKPOINT_ICONS[bp]}`}></i>
                    </button>
                ))}
            </div>
            )}

            {onStylePatch && (
                <ArrangeSection node={node} current={current} breakpoint={breakpoint} onStylePatch={onStylePatch} onAlign={onAlign} />
            )}

            {onStylePatch && isImageType(node.type) && (
                <ImageSection node={node} current={current} breakpoint={breakpoint} onStylePatch={onStylePatch} />
            )}

            <TokenField label="Background Color" tokens={[{ label: 'None', value: '' }, ...COLOR_TOKENS]} value={current.backgroundColor} onChange={(v) => set('backgroundColor', v)} />
            <TokenField label="Background Gradient" tokens={GRADIENT_TOKENS} value={current.backgroundImage} onChange={(v) => set('backgroundImage', v)} />
            <TokenField label="Text Color" tokens={[{ label: 'None', value: '' }, ...COLOR_TOKENS]} value={current.color} onChange={(v) => set('color', v)} />
            <TokenField label="Font Family" tokens={[{ label: 'Default', value: '' }, ...FONT_TOKENS]} value={current.fontFamily} onChange={(v) => set('fontFamily', v)} />
            <TokenField label="Font Size" tokens={[{ label: 'Default', value: '' }, ...FONT_SIZE_TOKENS]} value={current.fontSize} onChange={(v) => set('fontSize', v)} />
            <TokenField label="Font Weight" tokens={[{ label: 'Default', value: '' }, ...FONT_WEIGHT_TOKENS]} value={current.fontWeight} onChange={(v) => set('fontWeight', v)} />

            <div className="lb-style-row">
                <label>Text Align</label>
                <div className="lb-align-buttons">
                    {ALIGN_TOKENS.map((a) => (
                        <button
                            key={a}
                            type="button"
                            className={`lb-align-btn ${current.textAlign === a ? 'active' : ''}`}
                            onClick={() => set('textAlign', current.textAlign === a ? '' : a)}
                            title={a}
                        >
                            <i className={`fas fa-align-${a}`}></i>
                        </button>
                    ))}
                </div>
            </div>

            <TokenField label="Padding" tokens={[{ label: 'Default', value: '' }, ...SPACING_TOKENS]} value={current.padding} onChange={(v) => set('padding', v)} />
            <TokenField label="Margin" tokens={[{ label: 'Default', value: '' }, ...SPACING_TOKENS]} value={current.margin} onChange={(v) => set('margin', v)} />
            <TokenField label="Border Radius" tokens={[{ label: 'Default', value: '' }, ...RADIUS_TOKENS]} value={current.borderRadius} onChange={(v) => set('borderRadius', v)} />
            <TokenField label="Shadow" tokens={SHADOW_TOKENS} value={current.boxShadow} onChange={(v) => set('boxShadow', v)} />

            <div className="form-group">
                <label>Width</label>
                <CommitField placeholder="e.g. 100%, 320px" value={current.width || ''} onCommit={(v) => set('width', v)} />
            </div>
            <div className="form-group">
                <label>Height</label>
                <CommitField placeholder="e.g. auto, 200px" value={current.height || ''} onCommit={(v) => set('height', v)} />
            </div>
        </div>
    );
}

function TokenField({ label, tokens, value, onChange }) {
    return (
        <div className="form-group">
            <label>{label}</label>
            <select className="form-control" value={value || ''} onChange={(e) => onChange(e.target.value)}>
                {tokens.map((t) => <option key={t.label} value={t.value}>{t.label}</option>)}
            </select>
        </div>
    );
}

const ALIGN_BUTTONS = [
    ['left', 'fa-align-left', 'Align left'],
    ['center', 'fa-align-center', 'Centre horizontally'],
    ['right', 'fa-align-right', 'Align right'],
    ['top', 'fa-angle-double-up', 'Align top'],
    ['middle', 'fa-arrows-alt-v', 'Centre vertically'],
    ['bottom', 'fa-angle-double-down', 'Align bottom'],
];

const percentOf = (value) => (Number.isFinite(parseFloat(value)) ? Math.round(parseFloat(value)) : '');
const zIndexOf = (value) => (Number.isFinite(parseInt(value, 10)) ? parseInt(value, 10) : 3);

/**
 * CorelDRAW-style arrangement: switch a component between the normal page flow
 * and free placement, then position / rotate / layer it. Everything is stored
 * per breakpoint, so a design can float on desktop and stack on mobile.
 */
function ArrangeSection({ node, current, breakpoint, onStylePatch, onAlign }) {
    const floating = current.position === 'absolute';
    const patch = (p) => onStylePatch(node.id, breakpoint, p);
    const z = zIndexOf(current.zIndex);

    function setMode(mode) {
        if (mode === 'floating') {
            patch({
                position: 'absolute',
                left: current.left || '10%',
                top: current.top || '10%',
                width: current.width || '30%',
            });
        } else {
            patch({ position: 'static', top: '', right: '', bottom: '', left: '', zIndex: '', transform: '' });
        }
    }

    return (
        <div className="lb-arrange">
            <div className="form-group">
                <label>Position ({breakpoint})</label>
                <select className="form-control" value={floating ? 'floating' : 'flow'} onChange={(e) => setMode(e.target.value)}>
                    <option value="flow">In the page flow (stacks)</option>
                    <option value="floating">Free — place it anywhere with the mouse</option>
                </select>
            </div>

            {floating && (
                <>
                    <div className="lb-arrange-grid">
                        <label>X %
                            <CommitField type="number" value={percentOf(current.left)} onCommit={(v) => patch({ left: `${clampPercent(v)}%`, right: '' })} />
                        </label>
                        <label>Y %
                            <CommitField type="number" value={percentOf(current.top)} onCommit={(v) => patch({ top: `${clampPercent(v)}%`, bottom: '' })} />
                        </label>
                        <label>Angle °
                            <CommitField type="number" value={parseRotation(current.transform)} onCommit={(v) => patch({ transform: rotationValue(v) })} />
                        </label>
                    </div>

                    <div className="lb-arrange-row" role="group" aria-label="Align to the container">
                        {ALIGN_BUTTONS.map(([how, icon, title]) => (
                            <button key={how} type="button" title={title} aria-label={title} onClick={() => onAlign && onAlign(node.id, how)}>
                                <i className={`fas ${icon}`}></i>
                            </button>
                        ))}
                    </div>

                    <div className="lb-arrange-row lb-arrange-order" role="group" aria-label="Layer order">
                        <button type="button" onClick={() => patch({ zIndex: '100' })}>To front</button>
                        <button type="button" onClick={() => patch({ zIndex: String(z + 1) })}>Forward</button>
                        <button type="button" onClick={() => patch({ zIndex: String(Math.max(0, z - 1)) })}>Backward</button>
                        <button type="button" onClick={() => patch({ zIndex: '0' })}>To back</button>
                    </div>
                    <p className="lb-arrange-hint">Drag it on the canvas, use the arrow keys (Shift = bigger steps), or type exact values. Alt while dragging turns snapping off.</p>
                </>
            )}
        </div>
    );
}

/**
 * How a picture is sized and cropped. Dragging the corner handle on the canvas
 * keeps its proportions; these are the exact controls: quick sizes, how it
 * fills its box (crop / whole picture / stretch), and — for a cropped picture —
 * a mouse-draggable focus point. All per device size.
 */
function ImageSection({ node, current, breakpoint, onStylePatch }) {
    const patch = (p) => onStylePatch(node.id, breakpoint, p);
    const src = node.props?.src;
    const fit = current.objectFit || '';
    const { x, y } = parseObjectPosition(current.objectPosition);

    return (
        <div className="lb-image-section">
            <div className="form-group">
                <label>Picture size ({breakpoint})</label>
                <div className="lb-arrange-row" role="group" aria-label="Picture size">
                    <button type="button" onClick={() => patch({ width: '100%', height: '' })}>Full width</button>
                    <button type="button" onClick={() => patch({ height: '' })}>Auto height</button>
                    <button type="button" onClick={() => patch({ width: 'auto', height: 'auto', objectFit: '', objectPosition: '' })}>Original size</button>
                </div>
                <p className="lb-arrange-hint">Drag the corner handle on the canvas to resize it in proportion (hold Shift to change width and height separately).</p>
            </div>

            <div className="form-group">
                <label>How it fills its box</label>
                <select className="form-control" aria-label="Picture fit" value={fit} onChange={(e) => patch({ objectFit: e.target.value })}>
                    {FIT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
            </div>

            {src && fit !== 'fill' && (
                <div className="form-group">
                    <label>Focus — drag inside the picture ({x}% · {y}%)</label>
                    <FocalPad
                        src={src}
                        x={x}
                        y={y}
                        size={fit === 'contain' ? 'contain' : 'cover'}
                        onCommit={(nx, ny) => patch({ objectPosition: objectPositionValue(nx, ny), ...(fit ? {} : { objectFit: 'cover' }) })}
                    />
                </div>
            )}
        </div>
    );
}
